import { useState, useEffect, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Trash2, MapPin } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

const RADIUS_MIN = 0.1
const RADIUS_MAX = 25
const DEBOUNCE_DELAY_MS = 500

interface Airport {
  readonly id: string
  readonly name: string
  readonly address: string
  readonly radius_miles: number
}

interface AirportListItemProps {
  readonly airport: Airport
  readonly onUpdateRadius: (id: string, radius: number) => void
  readonly onRemove: (id: string) => void
  readonly isRemoving?: boolean
}

export function AirportListItem({
  airport,
  onUpdateRadius,
  onRemove,
  isRemoving = false,
}: AirportListItemProps) {
  const [radiusValue, setRadiusValue] = useState(String(airport.radius_miles))

  // Sync local state when airport prop changes
  useEffect(() => {
    setRadiusValue(String(airport.radius_miles))
  }, [airport.radius_miles])

  // Debounced radius save
  useEffect(() => {
    const parsed = parseFloat(radiusValue)
    if (isNaN(parsed) || parsed < RADIUS_MIN || parsed > RADIUS_MAX) return
    if (parsed === airport.radius_miles) return

    const timer = setTimeout(() => {
      onUpdateRadius(airport.id, parsed)
    }, DEBOUNCE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [radiusValue, airport.id, airport.radius_miles, onUpdateRadius])

  const handleRadiusChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setRadiusValue(e.target.value)
    },
    []
  )

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{airport.name}</p>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-3 w-3 flex-shrink-0" />
          <p className="truncate">{airport.address}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <label
            htmlFor={`radius-${airport.id}`}
            className="text-sm text-muted-foreground whitespace-nowrap"
          >
            Radius:
          </label>
          <Input
            id={`radius-${airport.id}`}
            type="number"
            min={RADIUS_MIN}
            max={RADIUS_MAX}
            step={0.1}
            value={radiusValue}
            onChange={handleRadiusChange}
            className="w-20"
          />
          <span className="text-sm text-muted-foreground">mi</span>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={isRemoving}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Airport</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove {airport.name}? Bookings will no longer
                be automatically detected as airport transfers for this location.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onRemove(airport.id)}>
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  )
}
